import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectHorizontalDragGestures
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.RoundRect
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.luminance
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import banko.composeapp.generated.resources.Res
import banko.composeapp.generated.resources.*
import models.CareerGame
import models.RollDistribution
import models.ROLL_TOTAL_RANGE
import models.ScoreRace
import org.jetbrains.compose.resources.stringResource
import kotlin.math.roundToInt

/*
 * Chart building blocks for the stats screens. Everything is drawn natively (Canvas + plain
 * layouts) so the charts work identically on Android, iOS, desktop and web with no chart library.
 *
 * Conventions (shared with every chart here): 2dp lines, bars capped at 24dp with 4dp rounded
 * tips square at the baseline, hairline gridlines, a surface-colored ring around markers, text in
 * the theme's ink colors (never the series color), and a legend whenever there are two or more
 * series. Touch replaces hover: tap or drag a chart to inspect a point, with the values read out
 * in text beneath it.
 */

// Categorical series colors, assigned by table seat so a player keeps their color however the
// standings move. Validated as an ordered set for color-vision-deficiency separation against the
// app's light and dark card surfaces (worst adjacent CVD ΔE 9.1 light / 8.4 dark). A few light-mode
// hues sit under 3:1 contrast, so every chart pairs them with a text legend/readout.
private val SeriesColorsLight = listOf(
    0xFF2A78D6, 0xFFEB6834, 0xFF1BAF7A, 0xFFEDA100, 0xFFE87BA4, 0xFF008300, 0xFF4A3AA7, 0xFFE34948,
).map { Color(it) }
private val SeriesColorsDark = listOf(
    0xFF3987E5, 0xFFD95926, 0xFF199E70, 0xFFC98500, 0xFFD55181, 0xFF008300, 0xFF9085E9, 0xFFE66767,
).map { Color(it) }

/** The fixed series color for a table seat; seats past the palette fold into a neutral gray. */
@Composable
internal fun seriesColor(tableIndex: Int): Color {
    val dark = MaterialTheme.colorScheme.surface.luminance() < 0.5f
    val palette = if (dark) SeriesColorsDark else SeriesColorsLight
    return palette.getOrNull(tableIndex) ?: MaterialTheme.colorScheme.outline
}

/** The raised, rimmed card every stats section sits on — the same treatment as the recap cards. */
@Composable
internal fun StatsCard(
    modifier: Modifier = Modifier,
    content: @Composable ColumnScope.() -> Unit,
) {
    Surface(
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        modifier = modifier.fillMaxWidth(),
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 14.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
            content = content,
        )
    }
}

/** Section heading in the display face, matching the game-over screen's "TABLE RECAP". */
@Composable
internal fun StatsSectionTitle(text: String, modifier: Modifier = Modifier) {
    Text(
        text = text.uppercase(),
        fontFamily = theme.bankoDisplayFontFamily(),
        fontSize = 15.sp,
        letterSpacing = 1.5.sp,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        modifier = modifier,
    )
}

/** A KPI tile: sentence-case label, a large value, and an optional one-line detail. */
@Composable
internal fun StatTile(
    label: String,
    value: String,
    modifier: Modifier = Modifier,
    detail: String? = null,
) {
    Surface(
        shape = RoundedCornerShape(14.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        modifier = modifier,
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 10.dp),
            verticalArrangement = Arrangement.spacedBy(2.dp),
        ) {
            Text(
                text = label,
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = value,
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.onSurface,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            if (detail != null) {
                Text(
                    text = detail,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
    }
}

/** A small filled circle carrying a series' identity next to its (ink-colored) label. */
@Composable
internal fun SeriesDot(color: Color, size: Dp = 10.dp) {
    Box(
        modifier = Modifier
            .size(size)
            .clip(CircleShape)
            .background(color),
    )
}

/**
 * Cumulative score per player, round by round. The latest round is marked by default; tapping or
 * dragging moves the marker, and the legend underneath reads out every player's score there.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun ScoreRaceChart(
    race: ScoreRace,
    selfName: String?,
    modifier: Modifier = Modifier,
    chartHeight: Dp = 180.dp,
) {
    val lastIndex = race.roundNums.lastIndex
    var selectedIndex by remember(race) { mutableStateOf<Int?>(null) }
    val shownIndex = (selectedIndex ?: lastIndex).coerceIn(0, lastIndex)
    val colors = race.series.map { seriesColor(it.tableIndex) }
    val ringColor = MaterialTheme.colorScheme.surfaceContainerHigh
    val gridColor = MaterialTheme.colorScheme.outlineVariant
    val crosshairColor = MaterialTheme.colorScheme.onSurfaceVariant
    val yMax = niceCeiling(race.maxPoints)
    val startLabel = stringResource(Res.string.stats_score_race_start)
    val shownLabel = if (race.roundNums[shownIndex] == 0) {
        startLabel
    } else {
        stringResource(Res.string.stats_round_label, race.roundNums[shownIndex])
    }
    // Draw everyone else first so the local player's line sits on top where lines cross.
    val drawOrder = race.series.indices.sortedBy { race.series[it].playerName == selfName }
    val chartDescription = race.series.joinToString(prefix = "$shownLabel: ", separator = ", ") {
        "${it.playerName} ${it.points[shownIndex]}"
    }

    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(modifier = Modifier.fillMaxWidth()) {
            Column(
                modifier = Modifier
                    .height(chartHeight)
                    .padding(end = 6.dp),
                verticalArrangement = Arrangement.SpaceBetween,
                horizontalAlignment = Alignment.End,
            ) {
                AxisLabel(formatAxisValue(yMax))
                AxisLabel(formatAxisValue(yMax / 2))
                AxisLabel("0")
            }
            Column(modifier = Modifier.weight(1f)) {
                Canvas(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(chartHeight)
                        .pointerInput(race) {
                            detectTapGestures { offset ->
                                selectedIndex = chartIndexAt(offset.x, size.width.toFloat(), CHART_EDGE.toPx(), lastIndex)
                            }
                        }
                        .pointerInput(race) {
                            detectHorizontalDragGestures { change, _ ->
                                change.consume()
                                selectedIndex = chartIndexAt(
                                    change.position.x,
                                    size.width.toFloat(),
                                    CHART_EDGE.toPx(),
                                    lastIndex,
                                )
                            }
                        }
                        .semantics { contentDescription = chartDescription },
                ) {
                    val edge = CHART_EDGE.toPx()
                    val plotHeight = size.height - edge * 2
                    fun xAt(index: Int): Float =
                        if (lastIndex == 0) size.width / 2 else edge + (size.width - edge * 2) * index / lastIndex
                    fun yAt(points: Int): Float = edge + plotHeight * (1f - points.toFloat() / yMax)

                    drawGridlines(gridColor, edge, plotHeight)
                    if (selectedIndex != null) {
                        drawLine(
                            color = crosshairColor.copy(alpha = 0.6f),
                            start = Offset(xAt(shownIndex), 0f),
                            end = Offset(xAt(shownIndex), size.height),
                            strokeWidth = 1.dp.toPx(),
                        )
                    }
                    drawOrder.forEach { seriesIndex ->
                        val series = race.series[seriesIndex]
                        val path = Path()
                        series.points.forEachIndexed { index, points ->
                            if (index == 0) path.moveTo(xAt(index), yAt(points)) else path.lineTo(xAt(index), yAt(points))
                        }
                        drawPath(
                            path = path,
                            color = colors[seriesIndex],
                            style = Stroke(width = 2.dp.toPx(), cap = StrokeCap.Round, join = StrokeJoin.Round),
                        )
                    }
                    drawOrder.forEach { seriesIndex ->
                        val center = Offset(xAt(shownIndex), yAt(race.series[seriesIndex].points[shownIndex]))
                        drawCircle(color = ringColor, radius = 6.dp.toPx(), center = center)
                        drawCircle(color = colors[seriesIndex], radius = 4.dp.toPx(), center = center)
                    }
                }
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 4.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    AxisLabel(startLabel)
                    AxisLabel(stringResource(Res.string.stats_round_short, race.roundNums.last()))
                }
            }
        }

        Text(
            text = shownLabel,
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onSurface,
        )
        FlowRow(
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            race.series.forEachIndexed { seriesIndex, series ->
                Row(verticalAlignment = Alignment.CenterVertically) {
                    SeriesDot(colors[seriesIndex])
                    Spacer(modifier = Modifier.width(6.dp))
                    Text(
                        text = series.playerName,
                        style = MaterialTheme.typography.bodySmall,
                        fontWeight = if (series.playerName == selfName) FontWeight.Bold else FontWeight.Normal,
                        color = MaterialTheme.colorScheme.onSurface,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                    Spacer(modifier = Modifier.width(4.dp))
                    Text(
                        text = series.points[shownIndex].toString(),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
        }
        Text(
            text = stringResource(Res.string.stats_score_race_hint),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.8f),
        )
    }
}

/**
 * How often each dice total came up (bars) against what fair dice would give over the same number
 * of rolls (ink ticks). Tap a column to read out its numbers.
 */
@Composable
internal fun RollDistributionChart(
    distribution: RollDistribution,
    modifier: Modifier = Modifier,
    chartHeight: Dp = 132.dp,
) {
    val totals = ROLL_TOTAL_RANGE.toList()
    var selectedTotal by remember(distribution) { mutableStateOf<Int?>(null) }
    val barColor = seriesColor(0)
    val expectedColor = MaterialTheme.colorScheme.onSurface
    val gridColor = MaterialTheme.colorScheme.outlineVariant
    val rolls = distribution.totalRolls
    val yMax = maxOf(
        distribution.counts.maxOrNull() ?: 0,
        totals.maxOf { (distribution.expectedShareFor(it) * rolls).roundToInt() },
        1,
    ).toFloat()
    val readoutTotal = selectedTotal ?: distribution.mostCommonTotal ?: 7

    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Canvas(
            modifier = Modifier
                .fillMaxWidth()
                .height(chartHeight)
                .pointerInput(distribution) {
                    detectTapGestures { offset ->
                        val slot = size.width / totals.size.toFloat()
                        selectedTotal = totals[(offset.x / slot).toInt().coerceIn(0, totals.lastIndex)]
                    }
                }
                .semantics {
                    contentDescription = totals.joinToString { "${it}: ${distribution.countFor(it)}" }
                },
        ) {
            val slot = size.width / totals.size
            val barWidth = minOf(slot - 6.dp.toPx(), 24.dp.toPx()).coerceAtLeast(2.dp.toPx())
            val top = 6.dp.toPx()
            val baseline = size.height
            val plotHeight = baseline - top
            drawLine(
                color = gridColor,
                start = Offset(0f, baseline - 0.5f),
                end = Offset(size.width, baseline - 0.5f),
                strokeWidth = 1.dp.toPx(),
            )
            totals.forEachIndexed { index, total ->
                val centerX = slot * index + slot / 2
                val barHeight = plotHeight * distribution.countFor(total) / yMax
                val dimmed = selectedTotal != null && selectedTotal != total
                if (barHeight > 0f) {
                    drawTopRoundedBar(
                        color = if (dimmed) barColor.copy(alpha = 0.4f) else barColor,
                        left = centerX - barWidth / 2,
                        right = centerX + barWidth / 2,
                        top = baseline - barHeight,
                        bottom = baseline,
                    )
                }
                val expectedY = baseline - plotHeight * (distribution.expectedShareFor(total) * rolls).toFloat() / yMax
                drawLine(
                    color = expectedColor.copy(alpha = if (dimmed) 0.35f else 0.85f),
                    start = Offset(centerX - barWidth / 2 - 3.dp.toPx(), expectedY),
                    end = Offset(centerX + barWidth / 2 + 3.dp.toPx(), expectedY),
                    strokeWidth = 2.dp.toPx(),
                    cap = StrokeCap.Round,
                )
            }
        }
        Row(modifier = Modifier.fillMaxWidth()) {
            totals.forEach { total ->
                Text(
                    text = total.toString(),
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = if (total == readoutTotal) FontWeight.Bold else FontWeight.Normal,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.weight(1f),
                )
            }
        }
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            Box(
                modifier = Modifier
                    .size(width = 10.dp, height = 10.dp)
                    .clip(RoundedCornerShape(topStart = 3.dp, topEnd = 3.dp))
                    .background(barColor),
            )
            LegendText(stringResource(Res.string.dice_report_legend_rolled))
            Spacer(modifier = Modifier.width(8.dp))
            Box(
                modifier = Modifier
                    .size(width = 14.dp, height = 2.dp)
                    .background(expectedColor.copy(alpha = 0.85f)),
            )
            LegendText(stringResource(Res.string.dice_report_legend_expected))
        }
        Text(
            text = stringResource(
                Res.string.dice_report_selected,
                readoutTotal,
                distribution.countFor(readoutTotal),
                formatPercent(distribution.shareFor(readoutTotal)),
                formatPercent(distribution.expectedShareFor(readoutTotal)),
            ),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurface,
        )
    }
}

/**
 * The local player's recent scores, oldest to newest, with wins in the accent series color and
 * every other finish in neutral gray. Tap a bar to read out that game.
 */
@Composable
internal fun RecentScoresChart(
    games: List<CareerGame>,
    nowEpochSeconds: Long,
    modifier: Modifier = Modifier,
    chartHeight: Dp = 120.dp,
) {
    // Stored newest first; charts read left-to-right in time.
    val chronological = games.reversed()
    var selectedIndex by remember(games) { mutableStateOf<Int?>(null) }
    val winColor = seriesColor(0)
    val otherColor = MaterialTheme.colorScheme.outline
    val gridColor = MaterialTheme.colorScheme.outlineVariant
    val yMax = niceCeiling(chronological.maxOfOrNull { it.score } ?: 0)
    val readout = chronological.getOrNull(selectedIndex ?: chronological.lastIndex)

    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(modifier = Modifier.fillMaxWidth()) {
            Column(
                modifier = Modifier
                    .height(chartHeight)
                    .padding(end = 6.dp),
                verticalArrangement = Arrangement.SpaceBetween,
                horizontalAlignment = Alignment.End,
            ) {
                AxisLabel(formatAxisValue(yMax))
                AxisLabel("0")
            }
            Canvas(
                modifier = Modifier
                    .weight(1f)
                    .height(chartHeight)
                    .pointerInput(games) {
                        detectTapGestures { offset ->
                            if (chronological.isNotEmpty()) {
                                val slot = size.width / chronological.size.toFloat()
                                selectedIndex = (offset.x / slot).toInt().coerceIn(0, chronological.lastIndex)
                            }
                        }
                    }
                    .semantics {
                        contentDescription = chronological.joinToString { "${it.score}" }
                    },
            ) {
                if (chronological.isEmpty()) return@Canvas
                val slot = size.width / chronological.size
                val barWidth = minOf(slot - 4.dp.toPx(), 24.dp.toPx()).coerceAtLeast(2.dp.toPx())
                val top = 6.dp.toPx()
                val baseline = size.height
                drawGridlines(gridColor, top, baseline - top, includeTop = true)
                chronological.forEachIndexed { index, game ->
                    val centerX = slot * index + slot / 2
                    val barHeight = (baseline - top) * game.score / yMax
                    val dimmed = selectedIndex != null && selectedIndex != index
                    val color = if (game.won) winColor else otherColor
                    if (barHeight > 0f) {
                        drawTopRoundedBar(
                            color = if (dimmed) color.copy(alpha = 0.4f) else color,
                            left = centerX - barWidth / 2,
                            right = centerX + barWidth / 2,
                            top = baseline - barHeight,
                            bottom = baseline,
                        )
                    }
                }
            }
        }
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            SeriesDot(winColor, size = 8.dp)
            LegendText(stringResource(Res.string.career_scores_legend_win))
            Spacer(modifier = Modifier.width(8.dp))
            SeriesDot(otherColor, size = 8.dp)
            LegendText(stringResource(Res.string.career_scores_legend_other))
        }
        if (readout != null) {
            Text(
                text = stringResource(
                    Res.string.career_scores_selected,
                    ageLabel(nowEpochSeconds, readout.playedAtEpochSeconds),
                    ordinalLabel(readout.placement),
                    readout.playerCount,
                    readout.score,
                ),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurface,
            )
        }
    }
}

/** A labeled horizontal bar row: label, a bar scaled to [fraction] of the track, and the value. */
@Composable
internal fun HorizontalBarRow(
    label: String,
    value: String,
    fraction: Float,
    color: Color,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelLarge,
            color = MaterialTheme.colorScheme.onSurface,
            modifier = Modifier.width(40.dp),
        )
        Box(modifier = Modifier.weight(1f).height(14.dp)) {
            if (fraction > 0f) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth(fraction.coerceIn(0.02f, 1f))
                        .height(14.dp)
                        .clip(RoundedCornerShape(topEnd = 4.dp, bottomEnd = 4.dp))
                        .background(color),
                )
            }
        }
        Text(
            text = value,
            style = MaterialTheme.typography.labelLarge,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            textAlign = TextAlign.End,
            modifier = Modifier.width(32.dp),
        )
    }
}

/**
 * A two-part split bar (e.g. wins vs losses against a rival), separated by a 2dp surface gap.
 * Draws nothing when both sides are zero.
 */
@Composable
internal fun SplitBar(
    left: Int,
    right: Int,
    leftColor: Color,
    rightColor: Color,
    modifier: Modifier = Modifier,
) {
    if (left + right == 0) return
    Row(
        modifier = modifier
            .fillMaxWidth()
            .height(8.dp)
            .clip(RoundedCornerShape(4.dp)),
        horizontalArrangement = Arrangement.spacedBy(2.dp),
    ) {
        if (left > 0) {
            Box(modifier = Modifier.weight(left.toFloat()).height(8.dp).background(leftColor))
        }
        if (right > 0) {
            Box(modifier = Modifier.weight(right.toFloat()).height(8.dp).background(rightColor))
        }
    }
}

/**
 * Where a value sits on a low→high scale: a track in the surface variant, a primary fill up to the
 * value, and end labels. Used for the banking-style ("nerve") read.
 */
@Composable
internal fun ScaleMeter(
    fraction: Float,
    lowLabel: String,
    highLabel: String,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(10.dp)
                .clip(RoundedCornerShape(5.dp))
                .background(MaterialTheme.colorScheme.surfaceVariant),
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth(fraction.coerceIn(0.04f, 1f))
                    .height(10.dp)
                    .clip(RoundedCornerShape(5.dp))
                    .background(MaterialTheme.colorScheme.primary),
            )
        }
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            AxisLabel(lowLabel)
            AxisLabel(highLabel)
        }
    }
}

@Composable
private fun AxisLabel(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelSmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        maxLines = 1,
    )
}

@Composable
private fun LegendText(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodySmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
    )
}

/** Hairline gridlines at the bottom, middle and (optionally) top of the plot area. */
private fun DrawScope.drawGridlines(
    color: Color,
    top: Float,
    plotHeight: Float,
    includeTop: Boolean = true,
) {
    val fractions = if (includeTop) listOf(0f, 0.5f, 1f) else listOf(0.5f, 1f)
    fractions.forEach { fraction ->
        val y = top + plotHeight * fraction
        drawLine(
            color = color,
            start = Offset(0f, y),
            end = Offset(size.width, y),
            strokeWidth = 1.dp.toPx(),
        )
    }
}

/** A bar with 4dp rounded top corners and a square foot on the baseline. */
private fun DrawScope.drawTopRoundedBar(
    color: Color,
    left: Float,
    right: Float,
    top: Float,
    bottom: Float,
) {
    val radius = minOf(4.dp.toPx(), (bottom - top), (right - left) / 2)
    val corner = CornerRadius(radius, radius)
    val path = Path().apply {
        addRoundRect(
            RoundRect(
                left = left,
                top = top,
                right = right,
                bottom = bottom,
                topLeftCornerRadius = corner,
                topRightCornerRadius = corner,
                bottomRightCornerRadius = CornerRadius.Zero,
                bottomLeftCornerRadius = CornerRadius.Zero,
            ),
        )
    }
    drawPath(path = path, color = color)
}

@Composable
internal fun ordinalLabel(place: Int): String = when (place) {
    1 -> stringResource(Res.string.ordinal_1)
    2 -> stringResource(Res.string.ordinal_2)
    3 -> stringResource(Res.string.ordinal_3)
    else -> stringResource(Res.string.ordinal_n, place)
}

/** "Today", "Yesterday", "3 days ago"… — relative, so no time-zone handling is needed. */
@Composable
internal fun ageLabel(nowEpochSeconds: Long, thenEpochSeconds: Long): String {
    val days = ((nowEpochSeconds - thenEpochSeconds).coerceAtLeast(0) / 86_400L).toInt()
    return when {
        days < 1 -> stringResource(Res.string.age_today)
        days < 2 -> stringResource(Res.string.age_yesterday)
        days < 14 -> stringResource(Res.string.age_days, days)
        days < 60 -> stringResource(Res.string.age_weeks, days / 7)
        days < 365 -> stringResource(Res.string.age_months, days / 30)
        else -> stringResource(Res.string.age_years, days / 365)
    }
}

private val CHART_EDGE = 6.dp
